import { MarketplaceEmailSender, MarketplaceEmailSenderStatus } from '@spike/email-marketing-model';
import { FunctionComponent } from 'react';
import SenderFormModal from './SenderFormModal';
import UseCuddlesDefaultConfirmModal from './UseCuddlesDefaultConfirmModal';
import FailedState from './states/FailedState';
import PendingState from './states/PendingState';
import UnconfiguredState from './states/UnconfiguredState';
import VerifiedState from './states/VerifiedState';

/**
 * TEMPORARY visual-review helper. Renders any sender state with synthetic
 * data, bypassing the slice / BE entirely so designers and reviewers can see
 * each state without flipping rows in the database.
 *
 * Activated via the URL query param `?tab=sender&preview=<state>`. Valid
 * values:
 *   - `unconfigured`  → empty state + Cuddles-default preview card
 *   - `pending`       → pending state with mock instructions
 *   - `verified`      → verified state with Edit-in-card pattern
 *   - `failed`        → failed state with sample SES error
 *   - `setup-modal`   → SenderFormModal in setup mode over unconfigured
 *   - `edit-modal`    → SenderFormModal in edit mode over verified
 *   - `escape-modal`  → UseCuddlesDefaultConfirmModal over verified
 *
 * Buttons inside the previews are mostly no-op'd; the goal is visual review,
 * not flow testing. Remove this file (and the `?preview=` branch in
 * `EmailSenderHost`) before shipping.
 */
export type EmailSenderPreviewName =
    | 'unconfigured'
    | 'pending'
    | 'verified'
    | 'failed'
    | 'setup-modal'
    | 'edit-modal'
    | 'escape-modal';

const MOCK_EMAIL = 'marketing@petshop.com';

const mockSender = (status: MarketplaceEmailSenderStatus): MarketplaceEmailSender => ({
    enabled: status !== 'unconfigured',
    senderEmail: status === 'unconfigured' ? null : MOCK_EMAIL,
    status,
    verifiedAt: status === 'verified' ? '2026-06-15T18:42:00Z' : null,
    lastError:
        status === 'failed' ? 'The email address could not be verified. The domain may be on a blocklist.' : null,
    instructions: status === 'pending' ? `Check ${MOCK_EMAIL} for the AWS verification email and click the link.` : null
});

const noop = () => undefined;

interface EmailSenderPreviewProps {
    preview: EmailSenderPreviewName;
}

const EmailSenderPreview: FunctionComponent<EmailSenderPreviewProps> = ({ preview }) => {
    if (preview === 'pending') {
        return (
            <PendingState
                sender={mockSender('pending')}
                onEdit={noop}
                onEscape={noop}
            />
        );
    }
    if (preview === 'verified') {
        return (
            <VerifiedState
                sender={mockSender('verified')}
                onEdit={noop}
                onEscape={noop}
            />
        );
    }
    if (preview === 'failed') {
        return (
            <FailedState
                sender={mockSender('failed')}
                onEdit={noop}
                onEscape={noop}
            />
        );
    }
    if (preview === 'setup-modal') {
        return (
            <>
                <UnconfiguredState onSetup={noop} />
                <SenderFormModal
                    mode="setup"
                    initialEmail=""
                    onCancel={noop}
                    onSubmitted={noop}
                />
            </>
        );
    }
    if (preview === 'edit-modal') {
        return (
            <>
                <VerifiedState
                    sender={mockSender('verified')}
                    onEdit={noop}
                    onEscape={noop}
                />
                <SenderFormModal
                    mode="edit"
                    initialEmail={MOCK_EMAIL}
                    onCancel={noop}
                    onSubmitted={noop}
                />
            </>
        );
    }
    if (preview === 'escape-modal') {
        return (
            <>
                <VerifiedState
                    sender={mockSender('verified')}
                    onEdit={noop}
                    onEscape={noop}
                />
                <UseCuddlesDefaultConfirmModal
                    saving={false}
                    onCancel={noop}
                    onConfirm={noop}
                />
            </>
        );
    }
    // 'unconfigured' (default)
    return <UnconfiguredState onSetup={noop} />;
};

export default EmailSenderPreview;
